import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DialogFooter } from '@/components/ui/dialog';

/**
 * The confirm-the-tags screen (2026-09-01 spreadsheet-import design).
 *
 * A hand-kept rolodex can't be reviewed row by row — 600 rows is an hour of
 * scrolling, the exact friction onboarding exists to remove. Errors in these
 * files are structural instead: a misread column header puts all forty
 * venues on the wrong tag, not one row. So this screen has the planner
 * confirm the model's *reading* of her file — one line per block, its
 * vendor count next to it so a misread is visible at a glance — rather than
 * the rows themselves. Renaming a tag here relabels every vendor under that
 * block; skipping a block or sheet drops it from extraction entirely.
 *
 * Two model passes sit behind this screen and both run in the background on
 * the server (`vendor-imports.ts`): interpretation before this screen can
 * render at all, extraction after she confirms. Both are polled for, at the
 * same 2s interval, because both are the same shape of problem — a planner
 * looking at a progress state while a handful of sequential-ish model calls
 * run behind an id she doesn't otherwise interact with (`BatchProgressPage`
 * polls the same way for the same reason).
 */

// ---------------------------------------------------------------------------
// Types mirroring the backend's response shapes (`vendor-imports.ts`,
// `sheet-interpret.ts`). Not imported from `@vendorops/backend` directly —
// this codebase's frontend/backend type sharing goes through `AppType` for
// RPC inference (`lib/api.ts`); other pages (e.g. `ReviewPage.tsx`) likewise
// define their own local types for a route's JSON payload rather than
// reaching into backend internals for them.
// ---------------------------------------------------------------------------

interface InterpretedBlock {
  range: string;
  headerText: string | null;
  tags: string[];
  nameColumn: string;
  fieldColumns: { column: string; field: string }[];
  vendorCount: number;
  reading: string;
  negative: boolean;
}

interface InterpretedSheet {
  name: string;
  include: boolean;
  blocks: InterpretedBlock[];
}

interface Interpretation {
  sheets: InterpretedSheet[];
}

interface ImportResult {
  created: number;
  merged: number;
  skipped: { row: number; reason: string }[];
  total: number;
  failedSheets: string[];
}

type ImportStatus = 'interpreting' | 'awaiting_confirmation' | 'extracting' | 'done' | 'failed';

interface ImportStatusResponse {
  id: string;
  filename: string;
  status: ImportStatus;
  interpretation: Interpretation | null;
  result: ImportResult | null;
  error: string | null;
}

/** The planner's edits layered onto a confirmed block, kept client-side until
 *  confirm — except `neverContact`, which travels to the server as part of
 *  the block itself. The server derives who gets blocked from that field
 *  directly rather than from a list this screen computes (see the comment
 *  above `confirmImportSchema` in `vendor-imports.ts`): a separate
 *  client-computed `blockedTags` list used to be able to disagree with what
 *  she'd actually confirmed once tags were edited down to empty, and Yes
 *  would silently block nothing. `skipped` still never travels — skipped
 *  blocks are dropped from the payload entirely. */
interface EditableBlock extends InterpretedBlock {
  skipped: boolean;
  /** Only meaningful when `negative` is true. `undefined` means "not
   *  answered yet" — deliberately distinct from `false`, so confirming can
   *  require an actual answer rather than defaulting to one. */
  neverContact?: boolean;
}

interface EditableSheet {
  name: string;
  include: boolean;
  blocks: EditableBlock[];
}

function toEditable(interpretation: Interpretation): EditableSheet[] {
  return interpretation.sheets.map((s) => ({
    name: s.name,
    include: s.include,
    blocks: s.blocks.map((b) => ({ ...b, skipped: false })),
  }));
}

export default function InterpretationReview({
  importId,
  onCancel,
  onImported,
  onFallback,
}: {
  importId: string;
  onCancel: () => void;
  onImported: () => void;
  onFallback: () => void;
}) {
  const queryClient = useQueryClient();
  const [sheets, setSheets] = useState<EditableSheet[] | null>(null);
  const toastedDone = useRef(false);

  const { data } = useQuery({
    queryKey: ['vendor-import', importId],
    queryFn: async () => {
      const res = await client.api.vendors.imports[':id'].$get({ param: { id: importId } });
      if (!res.ok) throw new Error('Failed to load that import');
      return (await res.json()) as unknown as ImportStatusResponse;
    },
    // Polls while a model pass is in flight; stops once the planner has
    // something to confirm or a terminal state, the same shape
    // `BatchProgressPage` uses for staggered sends. Stopping the moment
    // interpretation lands also protects `sheets` below from being clobbered
    // by a refetch mid-edit.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'interpreting' || status === 'extracting' ? 2000 : false;
    },
  });

  // Seed the editable copy exactly once, the moment the model's reading
  // arrives. Never re-derived from `data` after that — that's what makes it
  // safe for `sheets` to hold edits the server doesn't know about yet.
  useEffect(() => {
    if (sheets === null && data?.interpretation) {
      setSheets(toEditable(data.interpretation));
    }
  }, [data, sheets]);

  useEffect(() => {
    if (data?.status === 'done' && data.result && !toastedDone.current) {
      toastedDone.current = true;
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      const r = data.result;
      toast.success(
        `Imported ${r.created} new${r.merged > 0 ? `, merged ${r.merged}` : ''}${
          r.skipped.length > 0 ? `, skipped ${r.skipped.length} duplicate` : ''
        }`,
      );
    }
  }, [data, queryClient]);

  const confirm = useMutation({
    mutationFn: async () => {
      if (!sheets) throw new Error('Nothing to confirm yet');
      // `neverContact` rides along on each block — the server derives the
      // blocked-tags list from it directly rather than trusting a separate
      // list computed here. Only `skipped` is stripped: a skipped block
      // contributes no vendors, so there's nothing left for the server to
      // read off it either way.
      const interpretation = {
        sheets: sheets.map((s) => ({
          name: s.name,
          include: s.include,
          blocks: s.blocks.filter((b) => !b.skipped).map(({ skipped: _skipped, ...block }) => block),
        })),
      };

      const res = await client.api.vendors.imports[':id'].confirm.$post({
        param: { id: importId },
        json: { interpretation },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? 'Could not confirm that import.');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-import', importId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function updateBlock(sheetIdx: number, blockIdx: number, patch: Partial<EditableBlock>) {
    setSheets((prev) =>
      prev
        ? prev.map((s, si) =>
            si !== sheetIdx
              ? s
              : {
                  ...s,
                  blocks: s.blocks.map((b, bi) => (bi !== blockIdx ? b : { ...b, ...patch })),
                },
          )
        : prev,
    );
  }

  function toggleSheetInclude(sheetIdx: number) {
    setSheets((prev) =>
      prev ? prev.map((s, si) => (si !== sheetIdx ? s : { ...s, include: !s.include })) : prev,
    );
  }

  // ---------------------------------------------------------------------
  // Interpreting: pass 1 still running.
  // ---------------------------------------------------------------------
  if (!data || data.status === 'interpreting') {
    return (
      <>
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Reading{data?.filename ? ` ${data.filename}` : ' that spreadsheet'}…
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ---------------------------------------------------------------------
  // Failed: interpretation OR extraction crashed, or the model couldn't make
  // sense of the file. A partial import is fine; a silent one isn't — but
  // total failure here means she has no confirmed reading to fall back on,
  // so the only honest offer is to try a different file, which may well
  // route to the column mapper if it turns out to be a clean export.
  // ---------------------------------------------------------------------
  if (data.status === 'failed') {
    return (
      <>
        <div className="space-y-3 py-4">
          <p className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {data.error ?? 'Something went wrong with that import.'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
          <Button onClick={onFallback}>Try a different file</Button>
        </DialogFooter>
      </>
    );
  }

  // ---------------------------------------------------------------------
  // Extracting: confirmed, pass 2 running.
  // ---------------------------------------------------------------------
  if (data.status === 'extracting') {
    return (
      <>
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Importing your vendors…
        </div>
        <DialogFooter>
          <Button variant="outline" disabled>
            Please wait
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ---------------------------------------------------------------------
  // Done: report created / merged / skipped, and name any sheet that failed
  // rather than folding it into a silent partial import.
  // ---------------------------------------------------------------------
  if (data.status === 'done') {
    const r = data.result;
    return (
      <>
        <div className="space-y-3 py-4">
          <p className="text-sm">
            Imported {r?.created ?? 0} new vendor{r?.created === 1 ? '' : 's'}
            {r && r.merged > 0 ? `, merged ${r.merged}` : ''}
            {r && r.skipped.length > 0
              ? `, skipped ${r.skipped.length} duplicate${r.skipped.length === 1 ? '' : 's'}`
              : ''}
            .
          </p>
          {r && r.failedSheets.length > 0 && (
            <p className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
              Couldn't read {r.failedSheets.length === 1 ? 'this sheet' : 'these sheets'}:{' '}
              {r.failedSheets.join(', ')}. Everything else imported — you can try again for just
              those.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onImported}>Done</Button>
        </DialogFooter>
      </>
    );
  }

  // ---------------------------------------------------------------------
  // awaiting_confirmation: the review itself.
  // ---------------------------------------------------------------------
  if (!sheets) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading…
      </div>
    );
  }

  // Only asks about a block whose vendors are actually about to be imported —
  // a skipped block or an excluded sheet contributes no vendors this round,
  // so there is nothing for "never contact these" to apply to yet.
  const unansweredNegatives = sheets.some(
    (s) => s.include && s.blocks.some((b) => !b.skipped && b.negative && b.neverContact === undefined),
  );
  const totalVendors = sheets.reduce(
    (n, s) => (s.include ? n + s.blocks.reduce((m, b) => (b.skipped ? m : m + b.vendorCount), 0) : n),
    0,
  );

  return (
    <>
      <div className="space-y-5 py-2">
        <p className="text-sm text-muted-foreground">
          Here's what we found in {data.filename}. Tags carry over exactly as written — rename or
          remove one below and it applies to every vendor under that block.
        </p>

        <div className="max-h-[55vh] space-y-5 overflow-y-auto pr-1">
          {sheets.map((sheet, sheetIdx) => {
            const sheetTotal = sheet.blocks.reduce((n, b) => (b.skipped ? n : n + b.vendorCount), 0);
            return (
              <div
                key={sheet.name}
                className={`space-y-2 ${sheet.include ? '' : 'opacity-50'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold">{sheet.name}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {sheetTotal} vendor{sheetTotal === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                      onClick={() => toggleSheetInclude(sheetIdx)}
                    >
                      {sheet.include ? 'Skip sheet' : 'Include sheet'}
                    </button>
                  </div>
                </div>

                {sheet.blocks.map((block, blockIdx) => (
                  <BlockRow
                    key={block.range}
                    block={block}
                    onChange={(patch) => updateBlock(sheetIdx, blockIdx, patch)}
                    sheetName={sheet.name}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {unansweredNegatives && (
          <p className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-xs text-muted-foreground">
            Answer "never send inquiries to these?" for every flagged block before confirming.
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={unansweredNegatives || totalVendors === 0 || confirm.isPending}
          onClick={() => confirm.mutate()}
        >
          {confirm.isPending ? 'Confirming…' : `Confirm & import ${totalVendors}`}
        </Button>
      </DialogFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// One block: its reading, its tags (editable), skip, and — when the model
// flagged it negative — the explicit do-not-contact question.
// ---------------------------------------------------------------------------

function BlockRow({
  block,
  sheetName,
  onChange,
}: {
  block: EditableBlock;
  sheetName: string;
  onChange: (patch: Partial<EditableBlock>) => void;
}) {
  const [editing, setEditing] = useState(false);

  // A block answered Yes with its last tag then removed would otherwise
  // still carry `neverContact: true` into confirm with nothing left to
  // block — the server's schema refuses that combination outright, but
  // reverting the answer here means she sees it as unanswered again
  // (re-triggering `unansweredNegatives`) instead of finding out from a
  // rejected confirm.
  function clearIfNowEmpty(nextTags: string[]): Partial<EditableBlock> {
    return nextTags.length === 0 && block.neverContact === true
      ? { tags: nextTags, neverContact: undefined }
      : { tags: nextTags };
  }

  function removeTag(idx: number) {
    onChange(clearIfNowEmpty(block.tags.filter((_, i) => i !== idx)));
  }

  function renameTag(idx: number, value: string) {
    const trimmed = value.trim();
    const next = [...block.tags];
    if (!trimmed) {
      next.splice(idx, 1);
    } else {
      next[idx] = trimmed;
    }
    onChange(clearIfNowEmpty(next));
  }

  return (
    <div
      className={`rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 ${
        block.skipped ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm">{block.reading}</p>
        <span className="shrink-0 text-xs text-muted-foreground">
          {block.vendorCount} vendor{block.vendorCount === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">tags:</span>
        {editing ? (
          <>
            {block.tags.map((tag, i) => (
              <span key={i} className="flex items-center gap-1">
                <Input
                  className="h-7 w-32 text-xs"
                  defaultValue={tag}
                  onBlur={(e) => renameTag(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
                <button
                  type="button"
                  aria-label={`Remove tag ${tag}`}
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => removeTag(i)}
                >
                  <X className="size-3.5" />
                </button>
              </span>
            ))}
          </>
        ) : (
          block.tags.map((tag, i) => (
            <Badge key={i} variant="secondary" className="font-normal">
              {tag}
            </Badge>
          ))
        )}

        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => onChange({ skipped: !block.skipped })}
          >
            {block.skipped ? 'Include' : 'Skip'}
          </button>
        </div>
      </div>

      {block.negative && !block.skipped && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-destructive/10 px-3 py-2">
          <p className="text-sm">
            <strong>{sheetName}</strong> — {block.vendorCount} vendor{block.vendorCount === 1 ? '' : 's'}.
            Never send inquiries to these?
            {block.tags.length === 0 && (
              <span className="block text-xs text-muted-foreground">
                Add a tag first — that's what gets blocked.
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={block.neverContact === true ? 'default' : 'outline'}
              disabled={block.tags.length === 0}
              onClick={() => onChange({ neverContact: true })}
            >
              Yes
            </Button>
            <Button
              type="button"
              size="sm"
              variant={block.neverContact === false ? 'default' : 'outline'}
              onClick={() => onChange({ neverContact: false })}
            >
              No
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
